import { z } from "zod";
import { placementChannelZones } from "@/hooks/use-placement-catalog";

const brandSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  avatarImageUrl: z.string().nullable(),
  country: z.string().nullable(),
  updatedAt: z.string(),
});

export const campaignFormSchema = z.object({
  brandId: z.string(),
  brand: brandSchema.nullable(),
  brandSearchQuery: z.string(),
  minimumPrice: z.string(),
  adFormat: z.string(),
  campaignName: z.string(),
  marketState: z.string(),
  placementChannels: z.array(z.string()),
  placementZones: z.array(z.string()),
  budgetStartDate: z.string(),
  budgetEndDate: z.string(),
  totalBudget: z.string(),
  pricingModel: z.string(),
  pacing: z.string(),
  dailyPacing: z.string(),
});

export const brandStepSchema = campaignFormSchema.extend({
  brandId: z.string().min(1, "Select a brand"),
  brand: brandSchema,
});

export const productsStepSchema = campaignFormSchema.extend({
  minimumPrice: z
    .string()
    .trim()
    .refine(
      (value) =>
        value.length === 0 ||
        (!Number.isNaN(Number(value)) && Number(value) >= 0),
      {
        message: "Enter a valid minimum price",
      },
    ),
});

export const adFormatStepSchema = campaignFormSchema.extend({
  adFormat: z.string().min(1, "Select an ad format"),
  campaignName: z.string().trim().min(1, "Enter a campaign name"),
});

export const marketsMerchantsStepSchema = campaignFormSchema.extend({
  marketState: z.string().min(1, "Select a state or province"),
});

export const placementStepSchema = campaignFormSchema
  .extend({
    placementChannels: z
      .array(z.string())
      .min(1, "Select at least one channel"),
    placementZones: z.array(z.string()).min(1, "Select at least one ad zone"),
  })
  .superRefine((values, context) => {
    const hasZoneForEverySelectedChannel = values.placementChannels.every(
      (channelId) => {
        const channelZones = placementChannelZones[channelId] ?? [];
        if (channelZones.length === 0) {
          return true;
        }

        return channelZones.some((zoneId) =>
          values.placementZones.includes(`${channelId}.${zoneId}`),
        );
      },
    );

    if (!hasZoneForEverySelectedChannel) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["placementZones"],
        message: "Select at least one ad zone for each selected channel",
      });
    }
  });

export const budgetStepSchema = campaignFormSchema
  .extend({
    budgetStartDate: z.string().trim().min(1, "Enter a start date"),
    budgetEndDate: z.string(),
    totalBudget: z
      .string()
      .trim()
      .min(1, "Enter a total budget")
      .refine((value) => Number(value.replace(/[^0-9.]/g, "")) >= 100, {
        message: "Budget must be at least $100",
      }),
    pricingModel: z.string().min(1, "Select a pricing model"),
    pacing: z.string(),
    dailyPacing: z.string(),
  })
  .superRefine((values, context) => {
    if (values.pacing === "even" && !values.budgetEndDate.trim()) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["budgetEndDate"],
        message: "Enter an end date for even pacing",
      });
    }

    if (values.pacing === "custom") {
      const dailyPacing = values.dailyPacing.trim();

      if (!dailyPacing) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["dailyPacing"],
          message: "Enter a daily pacing cap",
        });
      } else if (
        Number.isNaN(Number(dailyPacing)) ||
        Number(dailyPacing) <= 0
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["dailyPacing"],
          message: "Enter a daily pacing cap greater than $0",
        });
      }
    }
  });

export type CampaignFormValues = z.infer<typeof campaignFormSchema>;
